import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useGetProfessionalCountsByOffice } from '~/domain/Professionals/usecases/useGetProfessionalCountsByOffice';
import { useUserSelector } from '~/hooks/useUserSelector';
import { routes } from '~/routes';
import { useIsWorkerClassificationEnabled } from '~/selectors';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import { useRequireSignUpStep } from '../shared/useRequireSignUpStep';
import { SignUpStepWrapper } from '../shared/SignUpStepWrapper';
import { PROFESSIONAL_RADIUS_MILES } from './constants';
import { OfficeSetupForm } from './OfficeSetupForm';
import { OneMoreStepPopup } from './OneMoreStepPopup';

interface IUserLike {
  id?: string;
  address?: Array<{ state?: string | null }> | null;
}

const FALLBACK_TITLE = "Let's Finish Setting Up Your Office";

const HIGH_DENSITY_SUBTITLE =
  'Offices in your area already trust GoTu to secure coverage for their practices. Let’s finish your setup.';
const LOW_DENSITY_SUBTITLE =
  'We’re actively expanding our network in your area. Be a part of the offices leading the way.';

const PROFESSIONALS_NEARBY_THRESHOLD_DEFAULT = 50;

export const OfficeSetupPage = () => {
  useRequireSignUpStep(routes.signUpRedesign.officeSetup);

  const navigate = useNavigate();
  const user = useUserSelector() as IUserLike | null | undefined;
  const officeId = user?.id ?? '';
  const officeState = user?.address?.[0]?.state ?? 'your state';

  const { totalCount, isLoading, isError } = useGetProfessionalCountsByOffice(officeId, {
    distance: PROFESSIONAL_RADIUS_MILES,
  });

  // Remote Config drives the cutoff so PM can tune it without a deploy.
  // The getter returns 0 when Firebase has not resolved yet, so fall back to
  // the default here — otherwise `totalCount >= 0` would always be true.
  const remoteThreshold = RemoteConfigUtils.getProfessionalsNearbyThreshold();
  const threshold = remoteThreshold > 0 ? remoteThreshold : PROFESSIONALS_NEARBY_THRESHOLD_DEFAULT;

  const showFallback = !officeId || isError || isLoading;
  const isHighDensity = totalCount >= threshold;

  // Spec: while the count resolves, never flash "0 Professionals"; on error or
  // missing officeId, fall back to a generic title that doesn't block the form.
  const title = showFallback
    ? FALLBACK_TITLE
    : isHighDensity
      ? `${totalCount} Professionals Are Ready Nearby`
      : `${totalCount} Professionals Within ${PROFESSIONAL_RADIUS_MILES} Miles`;

  const subtitle = isHighDensity ? HIGH_DENSITY_SUBTITLE : LOW_DENSITY_SUBTITLE;

  const isDualModelState = Boolean(useIsWorkerClassificationEnabled());
  const [showOneMoreStep, setShowOneMoreStep] = useState(false);

  return (
    <SignUpStepWrapper
      step={3}
      backTo={routes.signUpRedesign.officeDetails}
      title={title}
      subtitle={subtitle}
    >
      <OfficeSetupForm
        isProfileComplete={!isDualModelState}
        onSuccess={() => {
          if (isDualModelState) {
            setShowOneMoreStep(true);
            return;
          }
          navigate('/dashboard');
        }}
      />
      <OneMoreStepPopup
        open={showOneMoreStep}
        state={officeState}
        onReview={() => navigate(routes.signUpRedesign.workerType)}
      />
    </SignUpStepWrapper>
  );
};
